/**
 * Data Erasure Panel (Phase 5 — Step 5.8).
 *
 * GDPR/CCPA/FERPA compliant "Delete all data" with confirmation.
 */

import React, { useState } from 'react';
import { clearState } from '@/persistence/db';
import { Modal, Button } from '../shared';

interface DataErasurePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DataErasurePanel({ isOpen, onClose }: DataErasurePanelProps): React.ReactElement {
  const [step, setStep] = useState<'confirm' | 'type' | 'done'>('confirm');
  const [typed, setTyped] = useState('');

  const handleErase = async (): Promise<void> => {
    if (typed !== 'DELETE') return;

    // Clear IndexedDB
    await clearState();

    // Clear localStorage
    try {
      localStorage.clear();
    } catch {
      // Ignore
    }

    // Clear sessionStorage
    try {
      sessionStorage.clear();
    } catch {
      // Ignore
    }

    setStep('done');
  };

  const handleClose = (): void => {
    if (step === 'done') {
      window.location.reload();
    } else {
      setStep('confirm');
      setTyped('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Data Erasure">
      <div className="data-erasure">
        {step === 'confirm' && (
          <>
            <div className="data-erasure__warning">
              <strong>Warning:</strong> This will permanently delete ALL your data, including:
              <ul>
                <li>All courses and attendance records</li>
                <li>All policies and settings</li>
                <li>All backups and feedback</li>
                <li>Profile and consent data</li>
              </ul>
              <p>This action <strong>cannot be undone</strong>.</p>
            </div>
            <Button variant="danger" size="md" onClick={() => setStep('type')}>
              I understand, proceed with erasure
            </Button>
          </>
        )}

        {step === 'type' && (
          <>
            <p>Type <strong>DELETE</strong> to confirm permanent data erasure:</p>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Type DELETE here"
              aria-label="Type DELETE to confirm permanent data erasure"
              autoFocus
            />
            <div className="data-erasure__actions">
              <Button variant="secondary" size="sm" onClick={() => { setStep('confirm'); setTyped(''); }}>Cancel</Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleErase}
                disabled={typed !== 'DELETE'}
              >
                Permanently Delete All Data
              </Button>
            </div>
          </>
        )}

        {step === 'done' && (
          <div className="data-erasure__done">
            <p>All data has been erased successfully.</p>
            <p>The page will reload to start fresh.</p>
            <Button variant="primary" size="md" onClick={handleClose}>Reload</Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
